/**
 * Product Action - WHAT Section
 *
 * Edit mode UI for assigning a product.
 * Used inline within AutomationCard when editing the "What" section.
 *
 * V2 Reference: v2/app/templates/components/client-reminder-product.hbs
 *
 * Fields:
 * - reminderOnId: Selected product ID
 * - lifeTime: Only run if account doesn't have product
 * - chargeForItem: Whether to charge for the product
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Checkbox } from 'src/components/Checkbox';
import { Stack } from 'src/components/Stack';
import { fetchProductOptions } from 'src/models/product/product';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';

import { ActionWhatSectionProps } from '../automationActions';

/**
 * Product WHAT Section - Edit mode
 */
export const ProductWhatSection: FC<ActionWhatSectionProps> = ({
  automation,
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  // Fetch available products
  const { data: products = [], isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => fetchProductOptions(),
    staleTime: 60000,
  });

  const productOptions = products.map((p) => ({
    value: p.id.toString(),
    label: p.name,
  }));

  return (
    <Stack spacing={2}>
      {/* Product selector */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box>{t('automations.product.label')}</Box>
        <Box sx={{ minWidth: 200 }}>
          <FormSelect
            name={`automations.${index}.reminderOnId`}
            options={productOptions}
            prompt={isLoading ? t('automations.common.loading') : t('automations.product.selectProduct')}
            onChange={(e) => {
              setFieldValue(`automations.${index}.reminderOnId`, e.target.value);
              // V2 sets reminderOnType to 'Exercisecom::Product'
              setFieldValue(`automations.${index}.reminderOnType`, 'Exercisecom::Product');
            }}
          />
        </Box>
      </Box>

      {/* One time per account option */}
      <Box>
        <Checkbox
          label={t('automations.product.onlyRunIfNoProduct')}
          checked={Boolean(automation.lifeTime)}
          onChange={(e) =>
            setFieldValue(`automations.${index}.lifeTime`, e.target.checked)
          }
        />
      </Box>

      {/* Charge option */}
      <Box>
        <Checkbox
          label={t('automations.product.chargeForProduct')}
          checked={Boolean(automation.chargeForItem)}
          onChange={(e) =>
            setFieldValue(`automations.${index}.chargeForItem`, e.target.checked)
          }
        />
      </Box>
    </Stack>
  );
};

/**
 * Product What Summary Component
 */
export const ProductWhatSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();
  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: () => fetchProductOptions(),
    staleTime: 60000,
  });

  if (!automation.reminderOnId) {
    return <i>{t('automations.product.noProductSelected')}</i>;
  }

  const product = products.find(
    (p) => p.id.toString() === automation.reminderOnId?.toString()
  );
  const productName = product?.name || `Product #${automation.reminderOnId}`;

  return (
    <>
      {t('automations.product.assign')} <b>{productName}</b>
      {automation.lifeTime && (
        <span style={{ color: 'gray', fontSize: '0.875em' }}>
          {' '}
          {t('automations.product.oncePerAccount')}
        </span>
      )}
    </>
  );
};
